/**
 * Custom Collection Filters JavaScript
 * Handles auto-expansion of collection filters and visibility management
 */

class CollectionFilters {
  constructor() {
    this.mobileDrawerOpened = false; // Track if drawer has been opened by user
    this.init();
  }

  init() {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => this.setup());
    } else {
      this.setup();
    }
  }

  setup() {
    // Fix mobile filters - close any auto-opened filter on page load
    this.fixMobileFiltersOnLoad();

    // Listen for mobile drawer opening and reset it
    this.observeMobileDrawer();

    // Only apply to desktop filters (min-width: 750px)
    if (window.innerWidth >= 750) {
      // Auto-expand all vertical filters
      this.expandAllFilters();

      // Re-expand filters after facet updates
      this.observeFacetUpdates();

      // Handle show-more button visibility
      this.handleShowMoreButtons();
    }

    // Re-check on window resize
    window.addEventListener('resize', () => {
      if (window.innerWidth >= 750) {
        this.expandAllFilters();
        this.handleShowMoreButtons();
      }
    });
  }

  /**
   * Fix mobile filters - ensure drawer opens to main menu, not a submenu
   */
  fixMobileFiltersOnLoad() {
    // Use setTimeout to ensure DOM is fully ready
    setTimeout(() => {
      // Remove menu-opening class from any mobile filter details
      const mobileFilterDetails = document.querySelectorAll('.mobile-facets__details.menu-opening, .mobile-facets__details[open]');
      mobileFilterDetails.forEach(detail => {
        detail.classList.remove('menu-opening');
        detail.removeAttribute('open');
      });

      // Remove submenu-open class from mobile-facets__main
      const mobileFacetsMain = document.querySelector('.mobile-facets__main.submenu-open');
      if (mobileFacetsMain) {
        mobileFacetsMain.classList.remove('submenu-open');
      }
    }, 50);
  }

  /**
   * Watch for mobile drawer opening and ensure it shows main menu
   */
  observeMobileDrawer() {
    // Watch for the mobile drawer summary being clicked
    const mobileDrawerSummary = document.querySelector('.mobile-facets__open-wrapper');
    if (mobileDrawerSummary) {
      mobileDrawerSummary.addEventListener('click', () => {
        // Mark that drawer has been opened by user
        if (!this.mobileDrawerOpened) {
          this.mobileDrawerOpened = true;
          // Only fix on first open
          setTimeout(() => {
            this.fixMobileFiltersOnLoad();
          }, 10);
        }
      });
    }

    // Watch for drawer closing to reset the flag
    const mobileDrawerClose = document.querySelector('.mobile-facets__close');
    if (mobileDrawerClose) {
      mobileDrawerClose.addEventListener('click', () => {
        this.mobileDrawerOpened = false;
      });
    }
  }

  /**
   * Expand all vertical filter details elements (DESKTOP ONLY)
   */
  expandAllFilters() {
    // Only target desktop filters, not mobile
    const filterDetails = document.querySelectorAll('.facets__disclosure-vertical:not(.mobile-facets__details)');

    filterDetails.forEach(detail => {
      // Open the details element. Only set it when needed: re-setting [open] is an
      // attribute mutation, which would re-trigger observeFacetUpdates in a loop.
      if (!detail.open) detail.setAttribute('open', '');

      const summary = detail.querySelector('summary');
      if (!summary || summary.dataset.alwaysOpen) return;
      summary.dataset.alwaysOpen = 'true';

      // Prevent the default toggle behavior
      summary.addEventListener('click', (e) => {
        e.preventDefault();
      });

      // The summary can't toggle here, so don't expose it as an expandable button.
      // Each group is still named for screen readers by its fieldset legend
      // (or role="group" aria-label for price).
      summary.setAttribute('tabindex', '-1');
      summary.setAttribute('aria-hidden', 'true');
    });
  }

  /**
   * Show all items when a filter has active selections (DESKTOP ONLY)
   */
  handleShowMoreButtons() {
    // Only target desktop filters, not mobile
    const filterDetails = document.querySelectorAll('.facets__disclosure-vertical:not(.mobile-facets__details)');

    filterDetails.forEach(detail => {
      const activeCheckboxes = detail.querySelectorAll('.facet-checkbox.active');

      if (activeCheckboxes.length > 0) {
        // Show all hidden items
        const hiddenItems = detail.querySelectorAll('.show-more-item.hidden');
        hiddenItems.forEach(item => {
          item.classList.remove('hidden');
        });

        // Hide the show more button
        const showMoreButton = detail.querySelector('show-more-button');
        if (showMoreButton) {
          showMoreButton.style.display = 'none';
        }
      }
    });
  }

  /**
   * Watch for facet updates and re-apply expansions (DESKTOP ONLY)
   */
  observeFacetUpdates() {
    // Only observe desktop facets container
    const facetsContainer = document.querySelector('.facets-container');
    if (!facetsContainer) return;

    // Use MutationObserver to detect when facets are updated
    const observer = new MutationObserver((mutations) => {
      // Only apply to desktop
      if (window.innerWidth < 750) return;

      // Check if facets were updated
      let facetsUpdated = false;
      mutations.forEach(mutation => {
        if (mutation.type === 'childList' || mutation.type === 'attributes') {
          facetsUpdated = true;
        }
      });

      if (facetsUpdated) {
        // Re-apply filter expansions after a short delay
        setTimeout(() => {
          this.expandAllFilters();
          this.handleShowMoreButtons();
        }, 100);
      }
    });

    // Start observing
    observer.observe(facetsContainer, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'class']
    });

    // Also listen for custom events that might indicate facet updates
    document.addEventListener('facets:updated', () => {
      if (window.innerWidth >= 750) {
        this.expandAllFilters();
        this.handleShowMoreButtons();
      }
    });
  }
}

// Initialize the collection filters
new CollectionFilters();
